/**
 * ProjectDailies.jsx - a project's historical daily reports (11/2/26), the
 * "Daily reports" tab. Rows come from GET /api/projects/:id/daily-history,
 * matched by the project's numbers; a local model read each email for the
 * summary and blockers, so the cause and lost hours are its reading - the
 * report text (click a row) is the evidence.
 */
import { useEffect, useState } from 'react';

const enc = encodeURIComponent;
const DOT = { G: '#2E7D32', Y: '#F9A825', R: '#C62828' };
const CAUSE = { customer: 'Customer', vendor: 'Vendor', fanuc: 'FANUC', global: 'GLOBAL', none: '', unknown: 'Unknown' };
const stopped = (r) => (r.blockers || []).filter((b) => b && b.stopped_work);

/** Header numbers: report days, G/Y/R, days a customer stopped the work, hours written down as lost. */
export function dailyStats(reports) {
  const days = (pred) => new Set(reports.filter(pred).map((r) => r.report_date)).size;
  return {
    days: days(() => true),
    yellowRed: days((r) => r.status_color === 'Y' || r.status_color === 'R'),
    customerStopped: days((r) => stopped(r).some((b) => b.cause === 'customer')),
    hoursLost: Math.round(reports.reduce((s, r) => s + stopped(r).reduce((t, b) => t + (Number(b.hours_lost) || 0), 0), 0)),
  };
}

const card = { background: '#fff', border: '1px solid #E6E8E1', borderRadius: 10, padding: '10px 14px' };
const stat = (label, value, color = '#0D0D0D') => (
  <div style={{ ...card, minWidth: 130 }}>
    <div style={{ fontSize: 9.5, fontWeight: 800, color: '#777', textTransform: 'uppercase', letterSpacing: 1 }}>{label}</div>
    <div style={{ fontSize: 20, fontWeight: 800, color }}>{value}</div>
  </div>
);

export default function ProjectDailies({ projectId }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [open, setOpen] = useState(null); // { id, body }
  const [onlyStops, setOnlyStops] = useState(false);

  useEffect(() => {
    let live = true;
    fetch(`/api/projects/${enc(projectId)}/daily-history`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((d) => live && setData(d))
      .catch(() => live && setErr("Couldn't load the daily reports."));
    return () => {
      live = false;
    };
  }, [projectId]);

  const toggle = async (id) => {
    if (open && open.id === id) return setOpen(null);
    setOpen({ id, body: null });
    const r = await fetch(`/api/projects/${enc(projectId)}/daily-history/${enc(id)}`).catch(() => null);
    const d = r && r.ok ? await r.json() : { body: '' };
    setOpen((o) => (o && o.id === id ? { id, body: d.body || '(no text)' } : o));
  };

  if (err) return <div style={{ color: '#B71C1C', fontSize: 12 }}>{err}</div>;
  if (!data) return <div style={{ fontSize: 12, color: '#777' }}>Loading daily reports…</div>;
  if (!data.reports.length)
    return (
      <div style={{ fontSize: 12, color: '#777' }}>
        No daily reports on file for {data.keys.length ? data.keys.join(' / ') : 'this project (it has no project or PO number)'}.
      </div>
    );

  const s = dailyStats(data.reports);
  const list = onlyStops ? data.reports.filter((r) => stopped(r).length) : data.reports;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {stat('Report days', s.days)}
        {stat('Yellow / red days', s.yellowRed, '#8A6D00')}
        {stat('Days the customer stopped us', s.customerStopped, '#B71C1C')}
        {stat('Hours written down as lost', s.hoursLost)}
      </div>
      <label style={{ fontSize: 11.5, display: 'flex', gap: 6, alignItems: 'center' }}>
        <input type="checkbox" checked={onlyStops} onChange={(e) => setOnlyStops(e.target.checked)} />
        Only days where work stopped
      </label>
      <div style={{ ...card, padding: 0 }}>
        {list.map((r) => (
          <div key={r.id} style={{ borderBottom: '1px solid #F3F4EF', padding: '8px 14px' }}>
            <button
              type="button"
              onClick={() => toggle(r.id)}
              style={{ all: 'unset', cursor: 'pointer', display: 'flex', gap: 10, alignItems: 'baseline', width: '100%' }}
            >
              <span
                title={r.status_color || 'no status'}
                style={{ width: 9, height: 9, borderRadius: 5, flexShrink: 0, background: DOT[r.status_color] || '#CFD8DC' }}
              />
              <span style={{ fontSize: 11, fontWeight: 800, whiteSpace: 'nowrap' }}>{r.report_date || '-'}</span>
              <span style={{ fontSize: 11, color: '#555', whiteSpace: 'nowrap' }}>{r.author_name || ''}</span>
              <span style={{ fontSize: 11.5, flex: 1 }}>{r.summary || r.subject}</span>
            </button>
            {stopped(r).map((b, i) => (
              <div key={i} style={{ fontSize: 11, color: '#B71C1C', margin: '4px 0 0 19px' }}>
                Stopped{CAUSE[b.cause] ? ` · ${CAUSE[b.cause]}` : ''}
                {b.hours_lost ? ` · ${b.hours_lost} h` : ''}: {b.text}
              </div>
            ))}
            {open && open.id === r.id && (
              <pre
                style={{
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'inherit',
                  fontSize: 11,
                  background: '#FAFBF7',
                  padding: 10,
                  margin: '8px 0 0 19px',
                }}
              >
                {open.body == null ? 'Loading…' : open.body}
              </pre>
            )}
          </div>
        ))}
      </div>
      <div style={{ fontSize: 10.5, color: '#888' }}>
        From the daily report emails (2022-2026), read by a local AI model. Causes and lost hours are its reading of each report; click a
        day for the engineer's own words.
      </div>
    </div>
  );
}
